import HitRecord from "models/view-models/search/hit-record";
import { CollectionUtils, RecordUtils } from "@rsm-hcd/javascript-core";
import { ConversationMessage } from "models/interfaces/conversations/conversation-message";
import { MessageRole } from "models/enumerations/conversations/message-role";
import { Record } from "immutable";
import { ConversationMessageStatus } from "models/view-models/conversations/conversation-message-status";
import { ensureMessagePartRecords } from "models/view-models/conversations/message-part-factory";
import { isNumber } from "lodash";
import ToolCallRecord from "models/view-models/conversations/tool-call-record";

const defaultValues: ConversationMessage = {
    citations: [],
    content: undefined,
    conversationId: undefined,
    chatFinishReason: undefined,
    id: undefined,
    index: undefined,
    messageId: undefined,
    parentId: undefined,
    parts: [],
    prompt: undefined,
    feedback: undefined,
    role: undefined,
    status: ConversationMessageStatus.Completed,
    createdOn: undefined,
    toolCalls: [],
};

export default class ConversationMessageRecord
    extends Record(defaultValues)
    implements ConversationMessage
{
    // Do NOT set properties on immutable records due to babel and typescript transpilation issue
    // See https://github.com/facebook/create-react-app/issues/6506

    // -----------------------------------------------------------------------------------------
    // #region Constructor
    // -----------------------------------------------------------------------------------------

    constructor(params?: Partial<ConversationMessage>) {
        if (params == null) {
            params = Object.assign({}, defaultValues);
        }

        if (CollectionUtils.hasValues(params.citations)) {
            params.citations = RecordUtils.ensureRecords(
                params.citations,
                HitRecord
            );
        }

        if (CollectionUtils.hasValues(params.parts)) {
            params.parts = ensureMessagePartRecords(params.parts);
        }
        if (params.toolCalls != null) {
            params.toolCalls = params.toolCalls?.map(
                (toolCall: ToolCallRecord) =>
                    RecordUtils.ensureRecord(toolCall, ToolCallRecord)
            );
        }

        params.role = parseRole(params.role);

        super(params);
    }

    // #endregion Constructor

    // -----------------------------------------------------------------------------------------
    // #region Public Methods
    // -----------------------------------------------------------------------------------------
    /**
     * Gets all citations from the current message back to, but not including,
     * the previous user message.
     */
    public getCitations(messages: ConversationMessageRecord[]): HitRecord[] {
        const currentIndex = messages.findIndex((m) => m.id === this.id);

        if (currentIndex < 0) {
            return this.citations ?? [];
        }

        const citations: HitRecord[] = [];
        const seen = new Set<string>();

        for (let i = currentIndex; i >= 0; i--) {
            const message = messages[i];

            if (i !== currentIndex && message.role === MessageRole.User) {
                break;
            }

            if (!CollectionUtils.hasValues(message.citations)) {
                continue;
            }

            for (const citation of message.citations) {
                if (citation.id == null || seen.has(citation.id)) {
                    continue;
                }

                seen.add(citation.id);
                citations.push(citation);
            }
        }

        return citations;
    }

    /**
     * Merges new values into the record and returns a new instance.
     *
     * @param {Partial<ConversationMessage>} values
     */
    public with(
        values: Partial<ConversationMessage>
    ): ConversationMessageRecord {
        return new ConversationMessageRecord(
            Object.assign(this.toJS(), values)
        );
    }

    // #endregion Public Methods
}

function parseRole(role: unknown): MessageRole | undefined {
    if (role == null) {
        return undefined;
    }

    if (isNumber(role)) {
        return role;
    }

    if (role === "User") {
        return MessageRole.User;
    }

    if (role === "System") {
        return MessageRole.System;
    }

    if (role === "Tool") {
        return MessageRole.Tool;
    }

    return MessageRole.Assistant;
}
